'use client';

import { memo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { Close } from '@zoomit/icons';
import { Breakpoints, Flex, IconButton, Spacing } from '@zoomit/primitives';
import { Button, cssVar, theme, Typography } from '@zoomit/zoomit-ds';

import { useBookmarkArticle } from '../../hooks/useArticleBookmarks';
import { useRemoveBookmark } from '../../hooks/useBookmarksManagement';
import type { BookmarkItemType } from '../../types/articles';
import type { AuthorType } from '../../types/shared';
import ArticleBookmarkAddForm from './ArticleBookmarkAddForm';
import ArticleBookmarkItem from './ArticleBookmarkItem';

type Props = {
  articleId?: string;
  closeModal: () => void;
  openDeleteModal: () => void;
  openListModal: () => void;
  bookmarkList: BookmarkItemType[];
  bookmarkedItems: string[];
  modal?: 'bookmark-list' | 'remove-bookmark' | null;
  authors?: AuthorType[];
};

type ArticleBookmarkForm = { lists: { [key: string]: boolean } };

const ArticleBookmarksModal = (props: Props) => {
  const {
    articleId,
    authors,
    closeModal,
    openDeleteModal,
    modal = null,
    openListModal,
    bookmarkList,
    bookmarkedItems,
  } = props;
  const removeBookmarkMutation = useRemoveBookmark();
  const [removeBookmarkId, setRemoveBookmarkId] = useState<string | null>(null);
  const formMethods = useForm<ArticleBookmarkForm>({
    defaultValues: {
      lists: bookmarkedItems.reduce((acc, key: string) => {
        return {
          ...acc,
          [key]: true,
        };
      }, {}),
    },
  });
  const { handleSubmit } = formMethods;
  const articleBookmarks = useBookmarkArticle(articleId as string, authors);

  const handleBookmark = (data: ArticleBookmarkForm) => {
    if (!articleId) return;
    const listIds = [];

    for (const key in data.lists) {
      if (data.lists[key]) {
        listIds.push(key);
      }
    }

    articleBookmarks.mutate({ listIds, articleId });
  };

  if (modal == null) return null;

  if (modal === 'remove-bookmark') {
    return (
      <>
        <VerticalSeparator spacing={{ mx: -Spacing.x6, mb: 'x4' }} />
        <Flex flexDirection="column" gap={Spacing.x2}>
          <Typography variant="h6" fontWeight={900} lineHeight={2} textColor={cssVar(theme.text)}>
            آیا از حذف این لیست مطمئن هستید؟
          </Typography>
          <Typography variant="body" lineHeight={1.85}>
            با حذف این لیست، مطالب ذخیره شده در لیست نیز حذف خواهند شد
          </Typography>
        </Flex>
        <Footer
          alignItems="center"
          justifyContent="flex-start"
          spacing={{ p: 'x4', mt: 'x8', mx: -Spacing.x6 }}
          gap={Spacing.x2}
        >
          <Button
            fontWeight="normal"
            onClick={() => {
              removeBookmarkId && removeBookmarkMutation.mutate(removeBookmarkId);
              openListModal();
            }}
            size="fit"
            spacing={{ px: 'x6' }}
            className="h-8"
            colorScheme="secondary"
          >
            بله، لیست و مطالب حذف شود
          </Button>
          <CloseButton
            fontWeight="normal"
            onClick={() => {
              openListModal && openListModal();
            }}
            colorScheme="secondary"
            variant="outline"
          >
            <DesktopVisible>خیر، حذف نشود</DesktopVisible>
            <MobileVisible>انصراف</MobileVisible>
          </CloseButton>
        </Footer>
      </>
    );
  }

  return (
    <>
      <VerticalSeparator spacing={{ mx: -Spacing.x6, mb: 2 }} />
      <BookmarkList>
        <BookmarkListItemWrapper className="add" key="add-form">
          <ArticleBookmarkAddForm />
        </BookmarkListItemWrapper>
        <FormProvider {...formMethods}>
          {bookmarkList.map((item: BookmarkItemType) => (
            <BookmarkListItemWrapper key={item.id}>
              <ArticleBookmarkItem
                {...item}
                openConfirmModal={() => {
                  setRemoveBookmarkId(item.id);
                  openDeleteModal();
                }}
              />
            </BookmarkListItemWrapper>
          ))}
        </FormProvider>
      </BookmarkList>
      <Footer
        alignItems="center"
        spacing={{ p: 'x4', mt: 'x8', mx: -Spacing.x6 }}
        gap={Spacing.x2}
        className="list-footer"
      >
        {articleId && (
          <Button
            isLoading={articleBookmarks.isLoading}
            fontWeight="normal"
            onClick={handleSubmit(handleBookmark)}
            className="w-40"
          >
            ذخیره
          </Button>
        )}
        <CloseButton
          fontWeight="normal"
          onClick={() => {
            closeModal();
          }}
          size="small"
          colorScheme="secondary"
          variant="outline"
        >
          بستن
        </CloseButton>
      </Footer>
    </>
  );
};

export const ArticleBookmarkHeader = ({
  closeModal,
  title = 'مطلب را به کدام لیست اضافه می کنید؟',
}: {
  closeModal: () => void;
  title?: string;
}) => {
  return (
    <ArticleBookmarkListHeaderWrapper
      justifyContent="space-between"
      alignItems="center"
      spacing={{ pb: 'x5' }}
    >
      <Title variant="h6">{title}</Title>
      <DesktopVisible>
        <IconButton onClick={() => closeModal()}>
          <Close size="md" fill={cssVar(theme.grey3)} />
        </IconButton>
      </DesktopVisible>
    </ArticleBookmarkListHeaderWrapper>
  );
};

const Footer = styled(Flex)`
  background-color: var(${theme.background1});
  margin-top: auto;

  @media (max-width: ${Breakpoints.md}) {
    justify-content: space-evenly;
    width: calc(100% + 4.8rem);
  }

  &.list-footer {
    @media (max-width: ${Breakpoints.md}) {
      width: 100%;
      position: absolute;
      bottom: 0rem;
      left: 2.6rem;
    }
  }
`;

const CloseButton = styled(Button)`
  @media (max-width: ${Breakpoints.md}) {
    width: 9.6rem;
  }
`;

const BookmarkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  height: 38rem;
  overflow-y: auto;

  @media (max-width: ${Breakpoints.md}) {
    width: 100%;
    height: 80%;
  }

  &::-webkit-scrollbar {
    width: 3px;
    padding-right: 10px;
  }

  &::-webkit-scrollbar-track {
    border-radius: var(${theme.radius.md});
  }

  &::-webkit-scrollbar-thumb {
    border-radius: var(${theme.radius.md});
    background-color: var(${theme.grey6});
  }
`;

const BookmarkListItemWrapper = styled.li`
  list-style: none;
  padding: 0.7rem 0;
  margin: 0;
  width: 100%;
  border-bottom: 1px solid var(${theme.grey6});
  height: 40px;
  display: flex;
  align-items: center;

  &.add {
    border-bottom: none;
    font-weight: 900;
  }
`;

const Title = styled(Typography)`
  position: relative;

  &::before {
    content: '';
    position: absolute;
    width: 4px;
    height: 100%;
    top: 0;
    right: -2.4rem;
    background-color: var(${theme.primary1});
  }
`;

const VerticalSeparator = styled(Flex)`
  width: calc(100% + 4.8rem);
  height: 1px;
  background-color: var(${theme.grey6});
  @media (max-width: ${Breakpoints.md}) {
    display: none;
  }
`;

const ArticleBookmarkListHeaderWrapper = styled(Flex)`
  position: relative;
  @media (max-width: ${Breakpoints.md}) {
    padding: 0;
  }
`;

const DesktopVisible = styled(Flex)`
  @media (max-width: ${Breakpoints.md}) {
    display: none;
  }
`;

const MobileVisible = styled(Flex)`
  @media (min-width: ${Breakpoints.md}) {
    display: none;
  }
`;

export default memo(ArticleBookmarksModal);
